import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Card, Grid, Menu } from '@mui/material'
import dayjs, { Dayjs } from 'dayjs'
import { useNavigate } from 'react-router-dom'
import queryString from 'query-string'

import { ContentWrapper } from '../../../../components/ContentWrapper'
import Helmet from '../../../../components/Helmet'
import HeaderContent from '../../../../components/Layouts/HeaderContent'
import useAxios, { axiosInstance } from '../../../../hooks/useAxios'
import DailyReportDetail from './components/DailyReportDetail'
import DailyReportFilter, {
  IFilterMapped,
} from './components/DailyReportFilter'
import PageUrls from '../../../../constants/page-urls'
import { ICostDailyReport, IMerchantDailyReport } from '../DailySalesReport'
import * as XLSX from 'xlsx'
import { message } from 'antd'
import { ONLY_DATE_FORMAT } from '../../../../constants/global'
import { mergeObjects, numberFormat } from '../../../../utils'
import { IReport } from '../DailySalesReport/components/DailySalesReportTable'
import { sumBy } from 'lodash'
import { EOrderDateType } from '../../../../types/order'
import { useUser } from '../../../../hooks/useUser'

const Menus: any[] = []

const DailyReportPage = () => {
  const navigate = useNavigate()
  const queryStr = queryString.parse(window.location.search)
  const { user } = useUser()
  const tableRef = useRef(null)

  const initFilter = {
    categories: queryStr.categories?.length
      ? String(queryStr.categories).split(',')
      : undefined,
    products: queryStr.products?.length
      ? String(queryStr.products).split(',')
      : undefined,
    productGroups: queryStr.productGroups?.length
      ? String(queryStr.productGroups).split(',')
      : undefined,
    distribution: queryStr.distribution?.length
      ? String(queryStr.distribution).split(',')
      : undefined,
    startDate: queryStr.startDate
      ? dayjs(String(queryStr.startDate)).startOf('day').format()
      : dayjs().format(),
    endDate: queryStr.endDate
      ? dayjs(String(queryStr.endDate)).endOf('day').format()
      : dayjs().format(),
    trackingProvider: (queryStr.trackingProvider as string) || '',
    providerStatus: (queryStr.providerStatus as string) || '',
    spacificStatus: queryStr.spacificStatus?.length
      ? String(queryStr.spacificStatus).split(',')
      : undefined,
    dateType: (queryStr.dateType as EOrderDateType) || EOrderDateType.ORDERDATE,
  }

  const [filter, setFilter] = useState<IFilterMapped>(initFilter)
  const [searchFilter, setSearchFilter] = useState<IFilterMapped>(initFilter)

  const onFilterChange = useCallback(
    (newFilter: IFilterMapped) => {
      setFilter(newFilter)
    },
    [setFilter],
  )

  const handleSearch = (newFilter: IFilterMapped) => {
    setSearchFilter(newFilter)
    const newQuery = queryString.stringify({ ...newFilter })
    navigate(`${PageUrls.Report.Index}?${newQuery}`, {
      replace: true,
    })
  }

  // useEffect(() => {
  //   handleSearch(filter)
  // }, [])

  const merchantDailyReports = useAxios<[IMerchantDailyReport]>({
    method: 'POST',
    url: '/report/merchant-daily-reports',
    data: {
      categories: searchFilter.categories,
      products: searchFilter.products,
      productGroups: searchFilter.productGroups,
      distribution: searchFilter.distribution,
      startDate: dayjs(searchFilter.startDate).startOf('day').format(),
      endDate: dayjs(searchFilter.endDate).endOf('day').format(),
      trackingProvider: searchFilter.trackingProvider,
      providerStatus: searchFilter.providerStatus,
      spacificStatus: searchFilter.spacificStatus?.includes('all')
        ? undefined
        : searchFilter.spacificStatus,
      dateType: searchFilter.dateType,
    },
  })

  const costDailyReport = useAxios<[ICostDailyReport]>({
    method: 'GET',
    url: '/report/cost-daily-reports',
    params: {
      startDate: dayjs(searchFilter.startDate).startOf('day').format(),
      endDate: dayjs(searchFilter.endDate).endOf('day').format(),
      distribution: searchFilter.distribution,
    },
  })

  const handleOnDownload = useCallback(
    async (date: Dayjs) => {
      const costObject = costDailyReport.data?.reduce(
        (acc: any, costDailyReportItem: ICostDailyReport) => {
          const date = dayjs(costDailyReportItem.date).format(ONLY_DATE_FORMAT)
          acc[date] = {
            costAmountTotal: costDailyReportItem.amount,
          }
          return acc
        },
        {},
      )

      const merchantObject = merchantDailyReports.data?.reduce(
        (acc: any, merchantDailyReportItem: IMerchantDailyReport) => {
          const date = dayjs(merchantDailyReportItem.date).format(
            ONLY_DATE_FORMAT,
          )
          acc[date] = merchantDailyReportItem
          return acc
        },
        {},
      )

      const reports = mergeObjects(costObject, merchantObject)

      const reportsArray: IReport[] = Object.keys(reports).map(
        (date: string, index: number) => {
          const merchantDailyReportItem = reports[date]
          return {
            ...merchantDailyReportItem,
            key: index,
          }
        },
      )
      const reportData = reportsArray
      // console.log(reportData)
      const rows = [
        ['วันที่', dayjs(filter.startDate).format(ONLY_DATE_FORMAT)],
        [
          'รายการสั่งซื้อ',
          `${numberFormat(sumBy(reportData, 'count'))}`,
          'รายการ',
        ],
        ['จำนวนชิ้น', `${numberFormat(sumBy(reportData, 'unit'))}`, 'ชิ้น'],
        ['ยอด COD', `${numberFormat(sumBy(reportData, 'codTotal'))}`, 'บาท'],
        ['จำนวน COD', `${numberFormat(sumBy(reportData, 'codCount'))}`, 'ชิ้น'],
        ['ยอดโอน', `${numberFormat(sumBy(reportData, 'bankTotal'))}`, 'บาท'],
        [
          'จำนวนโอน',
          `${numberFormat(sumBy(reportData, 'bankCount'))}`,
          'รายการ',
        ],
        ['ค่าคอม', `${numberFormat(sumBy(reportData, 'commission'))}`, 'บาท'],
        [
          'ต้นทุนสินค้า',
          `${user?.isNotAllowSeeCost ? '-' : numberFormat(sumBy(reportData, 'costTotal'))}`,
          'บาท',
        ],
        [
          'ค่าใช้จ่าย',
          `${numberFormat(sumBy(reportData, 'costAmountTotal'))}`,
          'บาท',
        ],
        ['รายรับ', `${numberFormat(sumBy(reportData, 'subTotal'))}`, 'บาท'],
        [
          'รายจ่าย',
          `${numberFormat(sumBy(reportData, 'costAmountTotal'))}`,
          'บาท',
        ],
        [
          'คงเหลือ',
          `${
            (
              sumBy(reportData, 'codTotal') +
              sumBy(reportData, 'bankTotal') -
              (sumBy(reportData, 'costAmountTotal') +
                sumBy(reportData, 'costTotal') +
                sumBy(reportData, 'commission'))
            ).toLocaleString() ?? 0
          }`,
          'บาท',
        ],
      ]

      const ws = XLSX.utils.json_to_sheet(rows)
      const wb = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(wb, ws, 'Sheet1')
      XLSX.writeFile(wb, `orderDailyReportDetails.xlsx`)
      message.success('ดาวน์โหลดสำเร็จ')
    },
    [costDailyReport, merchantDailyReports, filter],
  )

  return (
    <ContentWrapper>
      <Helmet title="รายงานประจำวัน" />
      <Grid container justifyContent="flex-start" alignItems="center">
        <Grid item>
          <HeaderContent
            goBack
            title="รายงานประจำวัน"
            routes={Menus}
            descriptions="รายงานประจำวัน"
          />
        </Grid>
      </Grid>
      <Card
        sx={{
          padding: 2,
        }}
      >
        <DailyReportFilter
          filter={filter}
          onFilterChange={onFilterChange}
          onSearch={handleSearch}
          onDownload={handleOnDownload}
        />
        <DailyReportDetail
          isLoading={
            merchantDailyReports.isLoading || costDailyReport.isLoading
          }
          merchantDailyReports={merchantDailyReports.data ?? []}
          costDailyReport={costDailyReport.data ?? []}
          ref={tableRef}
        />
      </Card>
    </ContentWrapper>
  )
}

export default DailyReportPage
